import React, { useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import { Box, Button, Card, CheckboxField, TextField, Typography, DateIntervalField, DateField, Alert } from "@curves/ui-base";
import { EmployeeSelect, FocusModeToggle } from "@icoach/components";
import { CyclePurchaseSummaryCard, SelectWithMenuItems, TermSection } from "@icoach/healthyCyclePurchase/components";
import { isValidDate, getFirstOfMonth, getLastOfMonth, parseDate, initialDate, getNowTime } from "@util/moment";
import { useCyclePurchaseEffectOrderData, useCyclePurchaseEffectOrderSubmit, useCyclePurchaseEffectRange } from "@icoach/healthyCyclePurchase/hooks";
import useDateRangeFilter from "@util/hook/useDateRangeFilter";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import { getErrorModuleStatus, refIsRequiredError } from "@util/utils";
import { EFFECT_CONTENT_TYPE } from "@constants/";

/**
 * 循環訂單暫停申請頁面
 * 獨立申請表單，使用變更單 API
 */
const CyclePurchaseSuspendPage = () => {
	const { cycleOrderID, effectID = 0 } = useParams();

	// 使用 hooks 管理資料
	const { cycleOrder, effectOrder, resource, memberID, clubID, shippingDay } = useCyclePurchaseEffectOrderData({
		cycleOrderID,
		effectID,
	});

	// 提交變更單 hook
	const { submit, validationErrors, clearValidationErrors } = useCyclePurchaseEffectOrderSubmit({
		effectID,
		memberID,
	});

	// 使用 hook 取得變更單的生效日期和下次出貨日期
	const { effectDate, nextShippingDate, getEffectRangeDebounced } = useCyclePurchaseEffectRange();

	// 使用日期區間過濾 hook，限制最大三個月
	const { dateRangeRef: suspendDateRef, getDateParams, validateStartDate, validateEndDate } = useDateRangeFilter({ maxRange: 3, rangeUnit: "months" });

	// refs
	const suspendReasonRef = useRef(null);
	const suspendNoteRef = useRef(null);
	const suspendConfirmRef = useRef(null);
	const termRef = useRef(null);
	const effectCreateDateRef = useRef(null);
	const employeeRef = useRef(null);

	// 聚焦模式 ref
	const focusModeRef = useRef(false);

	// 啟用無操作計時器
	useInactivityTimerToggle();

	// 根據 effectID 決定預設值來源：effectID = 0 使用 cycleOrder，否則使用 effectOrder
	const defaultSource = useMemo(() => {
		const isNewEffectOrder = Number(effectID) === 0;
		return isNewEffectOrder ? cycleOrder : effectOrder;
	}, [effectID, cycleOrder, effectOrder]);

	// 組合提交參數s
	const getParams = () => {
		let result = {
			effectType: [EFFECT_CONTENT_TYPE.STOP], // 暫停申請的 effectType
			effectDate,
			shippingDay, // 提交時帶入原本的出貨日設定
			clubID,
			memberID,
			cycleOrderID,
		};

		// 從暫停期間取得日期範圍
		if (suspendDateRef.current && suspendDateRef.current.getResult) {
			const dateRange = getDateParams();
			result.suspendStartDate = getFirstOfMonth(dateRange.startDate);
			result.suspendEndDate = getLastOfMonth(dateRange.endDate);
		}

		if (suspendReasonRef.current && suspendReasonRef.current.getResult) {
			result.suspendReason = suspendReasonRef.current.getResult();
		}

		if (suspendNoteRef.current && suspendNoteRef.current.getResult) {
			result.suspendNote = suspendNoteRef.current.getResult();
		}

		if (termRef.current && termRef.current.getResult) {
			result = Object.assign(result, termRef.current.getResult());
		}

		if (effectCreateDateRef.current && effectCreateDateRef.current.getResult) {
			result.effectCreateDate = effectCreateDateRef.current.getResult();
		}

		if (employeeRef.current && employeeRef.current.getResult) {
			result.employeeID = employeeRef.current.getResult() || 0;
		}

		return result;
	};

	// 處理暫停期間開始日期變化
	const handleSuspendStartDateChange = (startDate, endDate) => {
		console.log(startDate, endDate, isValidDate(startDate) && isValidDate(endDate));
		// 當日期變化時，重新計算 effectDate 和 nextShippingDate
		if (isValidDate(startDate) && isValidDate(endDate)) {
			const params = getParams();
			getEffectRangeDebounced({
				...params,
				suspendStartDate: startDate,
				suspendEndDate: endDate,
			});

			validateStartDate(startDate, endDate);
		}
	};

	// 處理暫停期間結束日期變化
	const handleSuspendEndDateChange = (endDate, startDate) => {
		console.log(startDate, endDate, isValidDate(startDate) && isValidDate(endDate));
		// 當日期變化時，重新計算 effectDate 和 nextShippingDate
		if (isValidDate(startDate) && isValidDate(endDate)) {
			const params = getParams();
			getEffectRangeDebounced({
				...params,
				suspendStartDate: startDate,
				suspendEndDate: endDate,
			});

			validateEndDate(endDate, startDate);
		}
	};

	const handleSubmit = () => {
		clearValidationErrors();
		const isError = refIsRequiredError(suspendDateRef, suspendReasonRef, suspendConfirmRef, termRef, effectCreateDateRef, employeeRef);
		if (!isError) {
			const params = getParams();
			submit(params);
		}
	};

	return (
		<Box className="container main-container-spacing cycle-purchase-suspend-page">
			<Box component="header" className="page-header">
				<Typography className="page-header-title" variant="h3">
					健康產品變更單
				</Typography>
			</Box>
			<FocusModeToggle ref={focusModeRef}>
				<CyclePurchaseSummaryCard sourceData={cycleOrder} />
				<Card className={"order-info-card mb-4"}>
					<Card.CardHeader title="申請暫停" className="bg-light-active" />
					<Card.CardContent className="p-4">
						<Grid spacing={2} container>
							<Grid xs={12} item>
								<Alert severity="info" variant="filled" color="info" className="alert-justify-content-start">
									如暫停超過三個月，請先辦理退訂
								</Alert>
							</Grid>
							<Grid xs={6} item>
								<DateIntervalField
									label="暫停期間"
									key={`${defaultSource?.suspendStartDate}-${defaultSource?.suspendEndDate}`}
									ref={suspendDateRef}
									startDateProps={{
										defaultValue: defaultSource?.suspendStartDate,
										minDate: getFirstOfMonth(getNowTime()),
										placeholder: "請選擇月份",
										onChange: handleSuspendStartDateChange,
									}}
									endDateProps={{
										defaultValue: defaultSource?.suspendEndDate,
										minDate: getFirstOfMonth(getNowTime()),
										placeholder: "請選擇月份",
										onChange: handleSuspendEndDateChange,
									}}
									views={["year", "month"]}
									openTo="month"
									format="YYYY/MM"
									mask="____/__"
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={6} item>
								<SelectWithMenuItems
									label="暫停原因"
									ref={suspendReasonRef}
									key={defaultSource?.suspendReason}
									defaultValue={defaultSource?.suspendReason}
									options={resource?.suspendReasonOptions ?? []}
									{...getErrorModuleStatus(validationErrors, "suspendReason")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={12} item>
								<TextField
									label="暫停原因補充說明"
									ref={suspendNoteRef}
									key={defaultSource?.suspendNote}
									defaultValue={defaultSource?.suspendNote}
									{...getErrorModuleStatus(validationErrors, "suspendNote")}
									minRows={3}
									multiline
									fullWidth
								/>
							</Grid>
							{isValidDate(nextShippingDate) && (
								<Grid xs={12} item>
									<CheckboxField ref={suspendConfirmRef} required>
										<CheckboxField.Checkbox value={true} label={`我已確認將於${parseDate(nextShippingDate, "YYYY [年] M [月]")}恢復出貨。`} />
									</CheckboxField>
								</Grid>
							)}
							<Grid xs={4} item>
								<DateField
									label="建單日期"
									ref={effectCreateDateRef}
									key={initialDate(defaultSource?.effectCreateDate)}
									defaultValue={initialDate(defaultSource?.effectCreateDate) || initialDate(getNowTime())}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<DateField label="變更生效日期" key={initialDate(effectDate)} defaultValue={initialDate(effectDate)} fullWidth required disabled />
							</Grid>
							<Grid xs={4} item>
								<EmployeeSelect label="擔當" ref={employeeRef} options={resource?.coachOptions ?? []} {...getErrorModuleStatus(validationErrors, "employeeID")} fullWidth required />
							</Grid>
						</Grid>
					</Card.CardContent>
				</Card>
				{/* 訂購條款 */}
				<TermSection className={"mb-4"} ref={termRef} nextShippingDate={nextShippingDate} errorModule={validationErrors} />
				<Box className={"text-right btn-group px-4 pb-3"}>
					<Button variant={"contained"} onClick={() => focusModeRef.current.checkAndExitFocusMode(handleSubmit)}>
						確認送出
					</Button>
				</Box>
			</FocusModeToggle>
		</Box>
	);
};

export default CyclePurchaseSuspendPage;
